// src/admin/pages/DocumentsPage.jsx
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortIcon from '@mui/icons-material/Sort';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';

import { apiFetch } from '../../utils/api';

/**
 * Interpret a server datetime string as *local* time, using the browser's timezone.
 * This avoids the UTC vs local shift you were seeing.
 */
function parseLocalDateTime(value) {
    if (!value) return null;
    if (value instanceof Date) return value;

    const str = String(value).trim();
    if (!str) return null;

    // Example from server: "2025-02-15T12:30:00.000Z"
    // Strip trailing "Z" or "+/-HH:MM" so we treat it as local time
    const withoutZone = str
        .replace(/Z$/, '') // remove trailing Z
        .replace(/([+-]\d{2}:\d{2})$/, ''); // remove trailing timezone offset

    const d = new Date(withoutZone);
    if (!Number.isNaN(d.getTime())) return d;

    // Fallback: let JS try its best
    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDate(value) {
    const d = parseLocalDateTime(value);
    if (!d) return '';

    return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

export default function DocumentsPage({ category, title, description }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortDirection, setSortDirection] = useState('desc');

    const [file, setFile] = useState(null);
    const [label, setLabel] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');

    const [deletingId, setDeletingId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);

    const fetchDocs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (category) params.set('category', category);

            const res = await apiFetch(
                `/api/admin/documents?${params.toString()}`,
                {
                    credentials: 'include',
                }
            );

            const data = await res.json().catch(() => ({}));

            if (!res.ok || data.error) {
                throw new Error(
                    Array.isArray(data.error)
                        ? data.error.join('; ')
                        : data.error || `Request failed with ${res.status}`
                );
            }

            setDocs(Array.isArray(data.rows) ? data.rows : []);
        } catch (e) {
            console.error('Failed to load documents', e);
            setError(e.message || 'Failed to load documents.');
        } finally {
            setLoading(false);
        }
    }, [category]);

    useEffect(() => {
        fetchDocs();
    }, [fetchDocs]);

    const sortedDocs = useMemo(() => {
        const copy = [...docs];
        copy.sort((a, b) => {
            const da = parseLocalDateTime(a.uploaded_at)?.getTime();
            const db = parseLocalDateTime(b.uploaded_at)?.getTime();

            if (!Number.isFinite(da) || !Number.isFinite(db)) {
                // Fall back to ID if parsing fails
                return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
            }

            return sortDirection === 'asc' ? da - db : db - da;
        });
        return copy;
    }, [docs, sortDirection]);

    const handleUpload = async (e) => {
        e.preventDefault();
        setUploadError('');
        setUploadSuccess('');
        setError('');

        if (!file) {
            setUploadError('Please choose a file to upload.');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('category', category);
            if (label.trim()) {
                formData.append('title', label.trim());
            }
            formData.append('file', file);

            const res = await apiFetch('/api/admin/documents', {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const body = await res.json().catch(() => ({}));

            if (!res.ok || body.error) {
                throw new Error(
                    Array.isArray(body.error)
                        ? body.error.join('; ')
                        : body.error || `Upload failed with status ${res.status}`
                );
            }

            setUploadSuccess('File uploaded successfully.');
            setFile(null);
            setLabel('');
            await fetchDocs();
        } catch (err) {
            console.error('Upload failed', err);
            setUploadError(err.message || 'Failed to upload file.');
        } finally {
            setUploading(false);
        }
    };

    const openDeleteDialog = (doc) => {
        if (!doc || !doc.id) return;
        setError('');
        setUploadSuccess('');
        setDocToDelete(doc);
        setConfirmOpen(true);
    };

    const closeDeleteDialog = () => {
        // Prevent closing while delete is in-flight
        if (deletingId) return;
        setConfirmOpen(false);
        setDocToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!docToDelete || !docToDelete.id) {
            setConfirmOpen(false);
            setDocToDelete(null);
            return;
        }

        const docId = docToDelete.id;

        setError('');
        setUploadSuccess('');
        setDeletingId(docId);

        try {
            const res = await apiFetch(`/api/admin/documents/${docId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            const body = await res.json().catch(() => ({}));

            if (!res.ok || body.error) {
                throw new Error(
                    Array.isArray(body.error)
                        ? body.error.join('; ')
                        : body.error || `Delete failed with status ${res.status}`
                );
            }

            setDocs((prev) => prev.filter((d) => d.id !== docId));
            setConfirmOpen(false);
            setDocToDelete(null);
        } catch (err) {
            console.error('Delete failed', err);
            setError(err.message || 'Failed to delete file.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 960,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
            }}
        >
            <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body1" color="text.secondary">
                        {description}
                    </Typography>
                )}
            </Box>

            {/* Upload */}
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Upload a file
                    </Typography>

                    <Box
                        component="form"
                        onSubmit={handleUpload}
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2,
                            alignItems: { xs: 'stretch', sm: 'center' },
                        }}
                    >
                        <TextField
                            label="Label (optional)"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            size="small"
                            sx={{ flex: 1, minWidth: 200 }}
                        />

                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {file ? file.name : 'Choose file'}
                            <input
                                type="file"
                                hidden
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    setFile(f || null);
                                }}
                            />
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={uploading}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {uploading ? 'Uploading…' : 'Upload'}
                        </Button>
                    </Box>

                    {uploadError && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {uploadError}
                        </Typography>
                    )}
                    {uploadSuccess && !uploadError && (
                        <Typography color="success.main" sx={{ mt: 1 }}>
                            {uploadSuccess}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* List */}
            <Card>
                <CardContent>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 2 }}
                        spacing={1}
                    >
                        <Typography variant="h6">Uploaded files</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<SortIcon />}
                                onClick={() =>
                                    setSortDirection((prev) =>
                                        prev === 'desc' ? 'asc' : 'desc'
                                    )
                                }
                            >
                                Date {sortDirection === 'desc' ? '↓' : '↑'}
                            </Button>
                            <IconButton
                                aria-label="Refresh"
                                size="small"
                                onClick={fetchDocs}
                                disabled={loading}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>

                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 4,
                            }}
                        >
                            <CircularProgress size={28} />
                        </Box>
                    ) : error ? (
                        <Typography color="error">{error}</Typography>
                    ) : !sortedDocs.length ? (
                        <Typography color="text.secondary">
                            No files have been uploaded yet.
                        </Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Uploaded</TableCell>
                                        <TableCell align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedDocs.map((doc) => (
                                        <TableRow key={doc.id} hover>
                                            <TableCell>{doc.title}</TableCell>
                                            <TableCell>
                                                {formatDate(doc.uploaded_at)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent="flex-end"
                                                >
                                                    {doc.url ? (
                                                        <Link
                                                            href={doc.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            underline="none"
                                                        >
                                                            <Button
                                                                size="small"
                                                                startIcon={
                                                                    <OpenInNewIcon />
                                                                }
                                                            >
                                                                Open
                                                            </Button>
                                                        </Link>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            (no URL)
                                                        </Typography>
                                                    )}

                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() =>
                                                            openDeleteDialog(doc)
                                                        }
                                                        disabled={
                                                            deletingId === doc.id
                                                        }
                                                    >
                                                        {deletingId === doc.id
                                                            ? 'Removing…'
                                                            : 'Remove'}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Confirm delete dialog */}
            <Dialog
                open={confirmOpen}
                onClose={closeDeleteDialog}
                aria-labelledby="delete-doc-dialog-title"
                aria-describedby="delete-doc-dialog-description"
            >
                <DialogTitle id="delete-doc-dialog-title">
                    Remove file?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-doc-dialog-description">
                        {`Are you sure you want to remove "${
                            docToDelete?.title || 'this file'
                        }"? This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={!!deletingId}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={!!deletingId}
                    >
                        {deletingId ? 'Removing…' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
